import { lazy, Suspense, useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowRight,
  Browser,
  Buildings,
  ChartLineUp,
  Check,
  Clock,
  Compass,
  Copy,
  DownloadSimple,
  Flask,
  GearSix,
  ListBullets,
  Play,
  Plus,
  PuzzlePiece,
  ShieldCheck,
  SignOut,
  StopCircle,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { ApiError, api, readableError, type SessionResponse, type SessionUser } from "./api";
import { BrandMark } from "./Brand";
import { evaluationTemplates } from "./evaluation-templates";
import { connectExtension, detectExtension, subscribeExtension, type ExtensionInfo } from "./extension-bridge";
import { ExtensionPage } from "./ExtensionPage";
import { GuideView } from "./Guide";
import type { Evaluation, EvaluationReport, EvaluationStatus, LocalEvent } from "./types";

type View = "guide" | "runs" | "new" | "configs" | "extension" | "stats";
type AuthState = "checking" | "signed-out" | "signed-in";
type CreatedEvaluation = Awaited<ReturnType<typeof api.createEvaluation>>;
type AutoConnection = {
  evaluationId: string;
  stage: "opening" | "pairing" | "waiting" | "starting" | "connected" | "failed";
  error?: string;
};

const statusMeta: Record<EvaluationStatus, { label: string; tone: string }> = {
  awaiting_extension: { label: "等待插件", tone: "neutral" },
  ready: { label: "可以开始", tone: "ready" },
  starting: { label: "正在启动", tone: "active" },
  running: { label: "评测中", tone: "active" },
  evaluating: { label: "归纳报告中", tone: "active" },
  stopping: { label: "正在停止", tone: "danger" },
  stopped: { label: "已停止", tone: "neutral" },
  completed: { label: "已完成", tone: "success" },
  failed: { label: "失败", tone: "danger" },
};

const terminalStatuses = new Set<EvaluationStatus>(["stopped", "completed", "failed"]);
const Statistics = lazy(() => import("./Statistics"));

export function App() {
  const standaloneExtension = window.location.pathname === "/extension";
  const standaloneGuide = window.location.pathname === "/guide";
  const [auth, setAuth] = useState<AuthState>("checking");
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const [view, setView] = useState<View>(() => initialView());
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [events, setEvents] = useState<LocalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const [draftTemplateId, setDraftTemplateId] = useState("");
  const [autoConnection, setAutoConnection] = useState<AutoConnection | null>(null);

  const selected = evaluations.find((item) => item.id === selectedId) ?? null;

  const loadEvaluations = useCallback(async () => {
    try {
      const response = await api.listEvaluations();
      setEvaluations(response.evaluations);
      setSelectedId((current) => current ?? response.evaluations[0]?.id ?? null);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) setAuth("signed-out");
      else setError(readableError(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessionWithRetry()
      .then((session) => {
        if (!session.authenticated) {
          setAuth("signed-out");
          setLoading(false);
          return;
        }
        clearAuthQuery("auth");
        setCurrentUser(session.user ?? null);
        setAuth("signed-in");
        return loadEvaluations();
      })
      .catch(() => {
        setAuth("signed-out");
        setLoading(false);
      });
  }, [loadEvaluations]);

  useEffect(() => {
    if (auth !== "signed-in" || !selectedId) return;
    let active = true;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const [detail, eventPayload] = await Promise.all([
          api.getEvaluation(selectedId),
          api.events(selectedId).catch(() => null),
        ]);
        if (!active) return;
        setEvaluations((items) => replaceEvaluation(items, detail.evaluation));
        setError(null);
        if (active && eventPayload) setEvents(eventPayload.local);
      } catch (caught) {
        if (active && !(caught instanceof ApiError && caught.status === 404)) setError(readableError(caught));
      } finally {
        refreshing = false;
      }
    };
    void refresh();
    if (selected && terminalStatuses.has(selected.status)) {
      return () => {
        active = false;
      };
    }
    const interval = setInterval(refresh, 1200);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [auth, selectedId, selected?.status]);

  if (standaloneExtension) return <ExtensionPage standalone />;
  if (standaloneGuide) {
    return (
      <div className="public-extension-shell">
        <header>
          <a className="login-brand" href="/" aria-label="EchoLab 首页"><span className="brand-mark" aria-hidden="true"><BrandMark /></span> EchoLab</a>
          <div className="public-header-actions">
            <a className="button button-secondary" href="/extension"><PuzzlePiece size={17} />插件下载</a>
            <a className="button button-primary" href="/">登录控制台<ArrowRight size={17} /></a>
          </div>
        </header>
        <main>
          <GuideView onNavigate={(destination) => window.location.assign(destination === "extension" ? "/extension" : "/")} />
        </main>
      </div>
    );
  }

  const navigate = (next: View) => {
    setView(next);
    if (next !== "guide") markGuideSeen();
  };

  const stopSelected = async () => {
    if (!selected || terminalStatuses.has(selected.status) || stopping) return;
    setStopping(true);
    setEvaluations((items) => replaceEvaluation(items, { ...selected, status: "stopping" }));
    try {
      const response = await api.stopEvaluation(selected.id);
      setEvaluations((items) => replaceEvaluation(items, response.evaluation));
      setError(null);
    } catch (caught) {
      setError(readableError(caught));
      await loadEvaluations();
    } finally {
      setStopping(false);
    }
  };

  const connectEvaluation = async (created: CreatedEvaluation) => {
    const evaluation = created.evaluation;
    setAutoConnection({ evaluationId: evaluation.id, stage: "opening" });
    try {
      const connected = await connectExtension({
        relayBaseUrl: "https://relay-echolab.tidemoviegame.com",
        sessionId: created.relay.sessionId,
        pairingCode: created.relay.pairingCode,
        targetUrl: evaluation.targetUrl,
      });
      if (connected.status.phase === "error") throw new Error(connected.status.error || "extension_connection_failed");
      setAutoConnection({ evaluationId: evaluation.id, stage: connected.status.phase === "pairing" ? "pairing" : "waiting" });

      for (let attempt = 0; attempt < 60; attempt += 1) {
        try {
          setAutoConnection({ evaluationId: evaluation.id, stage: "starting" });
          const started = await api.startEvaluation(evaluation.id);
          setEvaluations((items) => replaceEvaluation(items, started.evaluation));
          setAutoConnection({ evaluationId: evaluation.id, stage: "connected" });
          return;
        } catch (caught) {
          if (!(caught instanceof ApiError && caught.code === "browser_extension_not_connected")) throw caught;
          setAutoConnection({ evaluationId: evaluation.id, stage: "waiting" });
        }
        await delay(300);
      }
      throw new Error("extension_connection_timeout");
    } catch (caught) {
      const message = readableExtensionError(caught);
      setAutoConnection({ evaluationId: evaluation.id, stage: "failed", error: message });
      setError(message);
    }
  };

  if (auth === "checking") return <BootScreen />;
  if (auth === "signed-out") {
    return <LoginScreen onSuccess={(session) => {
      setCurrentUser(session.user ?? null);
      setAuth("signed-in");
      setLoading(true);
      void loadEvaluations();
    }} />;
  }

  const canStop = selected && !terminalStatuses.has(selected.status);

  return (
    <div className="app-shell">
      <Sidebar view={view} user={currentUser} onView={navigate} onLogout={async () => {
        await api.logout().catch(() => undefined);
        setCurrentUser(null);
        setAuth("signed-out");
        setEvaluations([]);
        setSelectedId(null);
      }} />
      <div className="workspace">
        <Topbar
          view={view}
          selected={selected}
          canStop={Boolean(canStop)}
          stopping={stopping}
          onStop={stopSelected}
          onNew={() => navigate("new")}
        />
        {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
        <main className={`content ${canStop ? "content-with-mobile-stop" : ""}`}>
          {view === "runs" && (
            <RunsView
              evaluations={evaluations}
              selected={selected}
              events={events}
              loading={loading}
              onSelect={setSelectedId}
              onNew={() => navigate("new")}
              onUpdate={(evaluation) => setEvaluations((items) => replaceEvaluation(items, evaluation))}
              onError={(caught) => setError(readableError(caught))}
              onRetry={async (evaluation) => {
                try {
                  setError(null);
                  const retried = await api.retryEvaluation(evaluation.id);
                  setEvaluations((items) => replaceEvaluation(items, retried.evaluation));
                  if (retried.relay) void connectEvaluation({ evaluation: retried.evaluation, relay: retried.relay });
                } catch (caught) {
                  setError(readableError(caught));
                }
              }}
              autoConnection={autoConnection?.evaluationId === selected?.id ? autoConnection : null}
              onReconnect={(evaluation) => {
                if (!evaluation.pairingCode) {
                  setError("这次评测的配对码已经失效，请新建评测。");
                  return;
                }
                void connectEvaluation({
                  evaluation,
                  relay: {
                    sessionId: evaluation.relaySessionId,
                    pairingCode: evaluation.pairingCode,
                    expiresAt: "",
                  },
                });
              }}
            />
          )}
          {view === "guide" && <GuideView onNavigate={(destination) => navigate(destination)} />}
          {view === "new" && (
            <NewEvaluation
              initialTemplateId={draftTemplateId}
              onCreated={(created) => {
                setEvaluations((items) => [created.evaluation, ...items]);
                setSelectedId(created.evaluation.id);
                navigate("runs");
                void connectEvaluation(created);
              }}
            />
          )}
          {view === "configs" && <Configurations onUseTemplate={(templateId) => {
            setDraftTemplateId(templateId);
            navigate("new");
          }} />}
          {view === "extension" && <ExtensionPage onCreateEvaluation={() => navigate("new")} />}
          {view === "stats" && <Suspense fallback={<RunsSkeleton />}><Statistics evaluations={evaluations} /></Suspense>}
        </main>
      </div>
      {canStop && (
        <button className="mobile-stop" onClick={stopSelected} disabled={stopping || selected.status === "stopping"}>
          <StopCircle size={20} weight="fill" />
          {stopping || selected.status === "stopping" ? "正在停止" : "立即停止评测"}
        </button>
      )}
    </div>
  );
}

function BootScreen() {
  return (
    <div className="boot-screen" aria-live="polite">
      <div className="brand-mark" aria-hidden="true"><BrandMark /></div>
      <strong>正在连接 EchoLab</strong>
      <span className="skeleton-line" />
    </div>
  );
}

function LoginScreen({ onSuccess }: { onSuccess: (session: SessionResponse) => void }) {
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [feishuBusy, setFeishuBusy] = useState(false);
  const [error, setError] = useState<string | null>(() => authErrorFromLocation());

  useEffect(() => {
    clearAuthQuery("auth_error");
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!inviteCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const session = await api.login(inviteCode.trim());
      onSuccess(session);
    } catch (caught) {
      setError(readableError(caught));
    } finally {
      setBusy(false);
    }
  };

  const loginWithFeishu = async () => {
    if (feishuBusy) return;
    setFeishuBusy(true);
    setError(null);
    try {
      const { authorizeUrl } = await api.prepareFeishuLogin();
      window.location.assign(authorizeUrl);
    } catch (caught) {
      setError(readableError(caught));
      setFeishuBusy(false);
    }
  };

  return (
    <div className="login-layout">
      <section className="login-story">
        <div className="login-brand"><span className="brand-mark" aria-hidden="true"><BrandMark /></span> EchoLab</div>
        <div className="story-copy">
          <p className="section-kicker">Every action leaves evidence.</p>
          <h1>让真实操作留下可复盘的证据。</h1>
          <p>模拟用户完成网页任务，记录每次产品回复，并由独立评估 Agent 归纳问题与结果。</p>
        </div>
        <div className="trust-row">
          <ShieldCheck size={22} />
          <span>浏览器控制仅限用户主动选择的标签页</span>
        </div>
      </section>
      <section className="login-panel">
        <form className="login-form" onSubmit={submit}>
          <div>
            <h2>进入评测控制台</h2>
            <p>公司成员可使用飞书登录，受邀测试用户也可以继续使用邀请码。</p>
          </div>
          <button
            className="button button-primary button-large login-method-button"
            type="button"
            disabled={feishuBusy}
            onClick={() => void loginWithFeishu()}
          >
            <Buildings size={19} weight="fill" />
            {feishuBusy ? "正在打开飞书" : "使用飞书登录"}
            <ArrowRight size={18} />
          </button>
          <div className="login-divider"><span>或使用邀请码</span></div>
          <label className="field">
            <span>邀请码</span>
            <input
              type="text"
              value={inviteCode}
              onChange={(event) => setInviteCode(event.target.value)}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={32}
              placeholder="ECHO-XXXX-XXXX-XXXX"
              required
            />
          </label>
          {error && <p className="inline-error"><WarningCircle size={17} />{error}</p>}
          <button className="button button-secondary button-large" disabled={busy}>
            {busy ? "正在验证" : "登录"}
            {!busy && <ArrowRight size={18} />}
          </button>
          <p className="form-footnote">两种方式都只创建 EchoLab 登录会话；服务端密钥不会进入前端或浏览器插件。<span className="login-help-links"><a href="/guide">查看新手指南</a><a href="/extension">下载与安装浏览器插件</a></span></p>
        </form>
      </section>
    </div>
  );
}

function authErrorFromLocation(): string | null {
  const code = new URL(window.location.href).searchParams.get("auth_error");
  return code ? readableError(new ApiError(code, 401)) : null;
}

function clearAuthQuery(name: "auth" | "auth_error") {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(name)) return;
  url.searchParams.delete(name);
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function initialView(): View {
  try {
    return window.localStorage.getItem("echolab_guide_seen") ? "runs" : "guide";
  } catch {
    return "guide";
  }
}

function markGuideSeen() {
  try {
    window.localStorage.setItem("echolab_guide_seen", "1");
  } catch {
    // The guide still works when browser storage is unavailable.
  }
}

function Sidebar({ view, user, onView, onLogout }: { view: View; user: SessionUser | null; onView: (view: View) => void; onLogout: () => void }) {
  const items: Array<{ id: View; label: string; icon: ReactNode; mobileClass?: string }> = [
    { id: "guide", label: "使用指南", icon: <ShieldCheck size={19} />, mobileClass: "mobile-tertiary" },
    { id: "runs", label: "评测任务", icon: <ListBullets size={19} /> },
    { id: "new", label: "新建评测", icon: <Plus size={19} /> },
    { id: "configs", label: "配置库", icon: <GearSix size={19} />, mobileClass: "mobile-secondary" },
    { id: "extension", label: "浏览器插件", icon: <PuzzlePiece size={19} /> },
    { id: "stats", label: "数据统计", icon: <ChartLineUp size={19} />, mobileClass: "mobile-secondary" },
  ];
  return (
    <aside className="sidebar">
      <div className="sidebar-brand"><span className="brand-mark brand-mark-small" aria-hidden="true"><BrandMark /></span><strong>EchoLab</strong></div>
      <nav aria-label="主要导航">
        {items.map((item) => (
          <button key={item.id} className={`${view === item.id ? "nav-item active" : "nav-item"} ${item.mobileClass ?? ""}`.trim()} onClick={() => onView(item.id)}>
            {item.icon}<span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="sidebar-system">
          <span>系统状态</span>
          <strong><i />服务正常</strong>
        </div>
        <div
          className="sidebar-user"
          aria-label={user ? `当前用户 ${user.displayName}，${loginMethodLabel(user.loginMethod)}，用户编号 ${user.userCode}` : "当前用户信息加载中"}
          title={user ? `${user.displayName} · ${loginMethodLabel(user.loginMethod)} · ID ${user.userCode}` : "当前用户信息加载中"}
        >
          <span className="user-avatar" aria-hidden="true">{userInitial(user?.displayName)}</span>
          <span className="sidebar-user-copy">
            <strong>{user?.displayName ?? "已登录用户"}</strong>
            <small>{user ? `${loginMethodLabel(user.loginMethod)} · ID ${user.userCode}` : "正在读取身份"}</small>
          </span>
        </div>
        <button className="nav-item logout" onClick={onLogout} title="退出当前账号"><SignOut size={19} /><span>退出登录</span></button>
      </div>
    </aside>
  );
}

function loginMethodLabel(method: SessionUser["loginMethod"]): string {
  return method === "feishu" ? "飞书登录" : "邀请码登录";
}

function userInitial(name?: string): string {
  const value = name?.trim();
  if (!value) return "我";
  const words = value.split(/\s+/).filter(Boolean);
  if (words.length > 1) return words.slice(0, 2).map((word) => Array.from(word)[0]).join("").toUpperCase();
  return Array.from(value)[0]?.toUpperCase() ?? "我";
}

function Topbar({
  view,
  selected,
  canStop,
  stopping,
  onStop,
  onNew,
}: {
  view: View;
  selected: Evaluation | null;
  canStop: boolean;
  stopping: boolean;
  onStop: () => void;
  onNew: () => void;
}) {
  const titles: Record<View, string> = {
    guide: "使用指南",
    runs: selected ? selected.productName : "评测任务",
    new: "新建评测",
    configs: "配置库",
    extension: "浏览器插件",
    stats: "数据统计",
  };
  return (
    <header className="topbar">
      <div>
        <span className="topbar-label">EchoLab 控制台</span>
        <div className="topbar-title-row">
          <h1>{titles[view]}</h1>
          {view === "runs" && selected && <StatusBadge status={selected.status} />}
        </div>
      </div>
      <div className="topbar-actions">
        {canStop && (
          <button className="button button-stop desktop-stop" onClick={onStop} disabled={stopping || selected?.status === "stopping"}>
            <StopCircle size={19} weight="fill" />
            {stopping || selected?.status === "stopping" ? "正在停止" : "立即停止"}
          </button>
        )}
        {view !== "new" && <button className="button button-primary" onClick={onNew}><Plus size={18} />新建评测</button>}
      </div>
    </header>
  );
}

function ErrorBanner({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="error-banner" role="alert">
      <WarningCircle size={20} weight="fill" />
      <span>{message}</span>
      <button aria-label="关闭错误提示" onClick={onClose}><X size={17} /></button>
    </div>
  );
}

function RunsView({
  evaluations,
  selected,
  events,
  loading,
  onSelect,
  onNew,
  onUpdate,
  onError,
  onRetry,
  autoConnection,
  onReconnect,
}: {
  evaluations: Evaluation[];
  selected: Evaluation | null;
  events: LocalEvent[];
  loading: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onUpdate: (evaluation: Evaluation) => void;
  onError: (error: unknown) => void;
  onRetry: (evaluation: Evaluation) => void;
  autoConnection: AutoConnection | null;
  onReconnect: (evaluation: Evaluation) => void;
}) {
  if (loading) return <RunsSkeleton />;
  if (!evaluations.length) return <EmptyRuns onNew={onNew} />;
  return (
    <div className="runs-layout">
      <section className="run-list panel" aria-label="评测任务列表">
        <div className="panel-heading">
          <div><h2>全部任务</h2><p>{evaluations.length} 次评测</p></div>
        </div>
        <div className="run-list-items">
          {evaluations.map((evaluation) => (
            <button
              key={evaluation.id}
              className={selected?.id === evaluation.id ? "run-row selected" : "run-row"}
              onClick={() => onSelect(evaluation.id)}
            >
              <span className="run-row-main"><strong>{evaluation.productName}</strong><span>{formatDate(evaluation.createdAt)}</span></span>
              <StatusBadge status={evaluation.status} />
            </button>
          ))}
        </div>
      </section>
      {selected && (
        <RunDetail
          evaluation={selected}
          events={events}
          onUpdate={onUpdate}
          onError={onError}
          onRetry={() => onRetry(selected)}
          autoConnection={autoConnection}
          onReconnect={() => onReconnect(selected)}
        />
      )}
    </div>
  );
}

function RunDetail({
  evaluation,
  events,
  onUpdate,
  onError,
  onRetry,
  autoConnection,
  onReconnect,
}: {
  evaluation: Evaluation;
  events: LocalEvent[];
  onUpdate: (evaluation: Evaluation) => void;
  onError: (error: unknown) => void;
  onRetry: () => void;
  autoConnection: AutoConnection | null;
  onReconnect: () => void;
}) {
  const [starting, setStarting] = useState(false);
  const canStart = evaluation.status === "ready";
  const waiting = evaluation.status === "awaiting_extension";

  const start = async () => {
    setStarting(true);
    try {
      const response = await api.startEvaluation(evaluation.id);
      onUpdate(response.evaluation);
    } catch (caught) {
      onError(caught);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="run-detail">
      {(waiting || evaluation.status === "ready") && (
        <section className="panel setup-panel">
          <div className="panel-heading">
            <div>
              <h2>{evaluation.status === "ready" ? "浏览器已经连接" : autoConnection ? "正在自动连接浏览器" : "连接浏览器"}</h2>
              <p>{evaluation.status === "ready" ? "目标网页已交给插件，可以启动用户模拟" : "插件会自动打开并只控制这次评测的目标网页"}</p>
            </div>
            <PuzzlePiece size={24} />
          </div>
          <ConnectionProgress status={evaluation.status} autoConnection={autoConnection} />
          <details className="manual-pairing">
            <summary>连接遇到问题？查看手动连接信息</summary>
            <div className="pair-grid">
              <CopyValue label="会话 ID" value={evaluation.relaySessionId} />
              <CopyValue label="配对码" value={evaluation.pairingCode || "已使用"} emphasis />
            </div>
            <ol className="setup-steps">
              <li><span>1</span><p>先在 Chrome 打开需要评测的网页。</p></li>
              <li><span>2</span><p>打开插件的“手动方式”，填入会话 ID 和配对码。</p></li>
              <li><span>3</span><p>连接成功后回到这里启动评测。</p></li>
            </ol>
          </details>
          <div className="setup-actions">
            {waiting && (!autoConnection || autoConnection.stage === "failed") ? (
              <button className="button button-secondary" onClick={onReconnect}><PuzzlePiece size={18} />重新自动连接</button>
            ) : (
              <a className="button button-secondary" href="/extension"><DownloadSimple size={18} />插件下载与更新</a>
            )}
            <button className="button button-primary" disabled={!canStart || starting} onClick={start}>
              <Play size={18} weight="fill" />
              {starting ? "正在启动" : canStart ? "开始评测" : autoConnection ? "正在自动连接" : "等待插件连接"}
            </button>
          </div>
        </section>
      )}

      {evaluation.status === "failed" && (
        <FailurePanel evaluation={evaluation} onRetry={onRetry} />
      )}

      <section className="detail-grid">
        <InfoBlock icon={<Browser size={20} />} label="被测页面" value={evaluation.targetUrl} link />
        <InfoBlock icon={<Play size={20} />} label="本次用户输入" value={evaluation.taskQuery} />
        <InfoBlock icon={<Compass size={20} />} label="产品期望体验" value={evaluation.experienceBrief} />
        <InfoBlock icon={<Flask size={20} />} label="用户场景" value={evaluation.scenarioName} />
        <InfoBlock icon={<ShieldCheck size={20} />} label="模拟用户" value={evaluation.personaName} />
        <InfoBlock
          icon={<Clock size={20} />}
          label="回复监听"
          value={`${evaluation.monitoringProfile === "nexplay" ? "Nexplay 长任务监听" : "通用页面监听"} · 单轮 ${Math.round(evaluation.responseDeadlineSeconds / 60)} 分钟`}
        />
        <InfoBlock icon={<Clock size={20} />} label="创建时间" value={formatDate(evaluation.createdAt)} />
      </section>

      <section className="panel timeline-panel">
        <div className="panel-heading"><div><h2>运行记录</h2><p>编排事件与 Agent 运行状态</p></div></div>
        <Timeline evaluation={evaluation} events={events} />
      </section>

      {evaluation.report && (
        <ReportPanel report={evaluation.report} />
      )}
    </div>
  );
}

function ReportPanel({ report }: { report: EvaluationReport }) {
  const metrics = recordValue(report.metrics) ?? {};
  const raw = recordValue(report.raw) ?? {};
  const coverage = recordValue(raw.coverage) ?? {};
  const findings = report.findings ?? [];
  const actions = recordList(report.priorityActions);
  const stages = recordList(raw.stage_breakdown);
  const limitations = stringList(raw.limitations);
  const confidence = textValue(raw.confidence);
  const metricItems = [
    { label: "场景完成率", value: percentValue(metrics.scenario_completion_rate), detail: textValue(metrics.scenario_completion_interpretation) },
    { label: "可评估轮次通过率", value: percentValue(metrics.evaluable_pass_rate) },
    { label: "上下文保持", value: percentValue(metrics.context_preservation_pass_rate) },
    { label: "严重门禁失败", value: numberText(metrics.critical_gate_failures) },
  ].filter((item) => item.value !== "-");

  return (
    <section className="panel report-panel">
      <header className="report-summary">
        <div className="score-value" aria-label={`评测得分 ${report.overallScore ?? "未评分"}`}>{report.overallScore ?? "-"}</div>
        <div className="report-summary-copy">
          <div className="report-title-row"><h2>{report.partial ? "部分评测报告" : "评测报告"}</h2><span>{report.partial ? "证据已保存" : confidence ? `置信度 · ${confidenceLabel(confidence)}` : "已完成"}</span></div>
          <p>{report.summary || "报告已生成"}</p>
          <div className="report-coverage" aria-label="评测覆盖范围">
            <span>{numberText(coverage.runs_completed)} 次完整运行</span>
            <span>{numberText(coverage.user_turns)} 轮用户交互</span>
            <span>{numberText(coverage.product_replies)} 次产品回复</span>
          </div>
        </div>
      </header>

      {metricItems.length > 0 && (
        <section className="report-section" aria-labelledby="report-metrics-title">
          <div className="report-section-heading"><h3 id="report-metrics-title">核心指标</h3><p>对照产品期望体验和本次多轮交互计算</p></div>
          <div className="report-metric-grid">
            {metricItems.map((item) => (
              <div className="report-metric" key={item.label}><span>{item.label}</span><strong>{item.value}</strong></div>
            ))}
          </div>
          {metricItems[0]?.detail && <p className="report-interpretation">{metricItems[0].detail}</p>}
        </section>
      )}

      {stages.length > 0 && (
        <section className="report-section" aria-labelledby="report-stage-title">
          <div className="report-section-heading"><h3 id="report-stage-title">阶段结果</h3><p>每一段用户路径的独立评价</p></div>
          <div className="report-stage-table">
            {stages.map((stage, index) => (
              <div className="report-stage-row" key={`${textValue(stage.stage)}-${index}`}>
                <strong>{textValue(stage.stage) || `阶段 ${index + 1}`}</strong>
                <span>{verdictLabel(textValue(stage.verdict))}</span>
                <b>{numberText(stage.score)} 分</b>
              </div>
            ))}
          </div>
        </section>
      )}

      {findings.length > 0 && (
        <section className="report-section" aria-labelledby="report-findings-title">
          <div className="report-section-heading"><h3 id="report-findings-title">发现的问题</h3><p>{findings.length} 项结论，区分工程 Bug、Agent 规则冲突与内容效果</p></div>
          <ol className="report-finding-list">
            {findings.map((finding, index) => (
              <li key={`${finding.title}-${index}`}>
                <div className="report-finding-meta"><span>{findingLabel(finding.category, finding.severity)}</span><b>{String(index + 1).padStart(2, "0")}</b></div>
                <div>
                  <h4>{finding.title || "待改进项"}</h4>
                  {finding.attributionStatus && <p><strong>归因置信</strong>{attributionStatusLabel(finding.attributionStatus)}</p>}
                  {finding.rootCauseBasis != null && <p><strong>归因依据</strong>{compactValue(finding.rootCauseBasis)}</p>}
                  {finding.alternativeCategories != null && <p><strong>竞争假设</strong>{compactValue(finding.alternativeCategories)}</p>}
                  {finding.diagnosticExchange != null && <p><strong>A2A 诊断</strong>{compactValue(finding.diagnosticExchange)}</p>}
                  {finding.evidence != null && <p><strong>证据</strong>{compactValue(finding.evidence)}</p>}
                  {finding.recommendation != null && <p><strong>建议</strong>{compactValue(finding.recommendation)}</p>}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {actions.length > 0 && (
        <section className="report-section" aria-labelledby="report-actions-title">
          <div className="report-section-heading"><h3 id="report-actions-title">优先改进行动</h3><p>按用户影响和可执行性排序</p></div>
          <ol className="report-action-list">
            {actions.map((action, index) => (
              <li key={`${textValue(action.action)}-${index}`}>
                <span>{numberText(action.priority) === "-" ? index + 1 : numberText(action.priority)}</span>
                <div>
                  <h4>{textValue(action.action) || "待确定改进行动"}</h4>
                  {textValue(action.user_impact) && <p><strong>用户影响</strong>{textValue(action.user_impact)}</p>}
                  {textValue(action.acceptance_condition) && <p><strong>验收标准</strong>{textValue(action.acceptance_condition)}</p>}
                  {stringList(action.likely_owners).length > 0 && <small>建议负责：{stringList(action.likely_owners).join(" / ")}</small>}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {limitations.length > 0 && (
        <section className="report-section report-limitations" aria-labelledby="report-limitations-title">
          <div className="report-section-heading"><h3 id="report-limitations-title">评测边界</h3><p>未观察到的结果不会被误判为产品缺陷</p></div>
          <ul>{limitations.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
        </section>
      )}

      <details className="report-raw">
        <summary>查看完整原始报告</summary>
        <pre>{JSON.stringify(report.raw ?? report, null, 2)}</pre>
      </details>
    </section>
  );
}

function findingLabel(category?: string, severity?: string): string {
  const group = category === "engineering_bug"
    ? "工程 Bug"
    : category === "agent_rule_conflict"
      ? "Agent 规则冲突"
      : category === "content_quality_problem"
        ? "内容效果"
        : category === "undetermined"
          ? "待确认归因"
          : category === "product_problem"
            ? "产品（旧分类）"
            : category === "content_problem"
              ? "内容（旧分类）"
              : category === "infrastructure_or_test_problem"
                ? "评测环境"
                : "问题";
  return `${group} · ${severityLabel(severity)}`;
}

function attributionStatusLabel(status?: string | null): string {
  return status === "confirmed" ? "已确认" : status === "probable" ? "较可能" : "待确认";
}

function ConnectionProgress({ status, autoConnection }: { status: EvaluationStatus; autoConnection: AutoConnection | null }) {
  if (autoConnection?.stage === "starting") {
    return (
      <div className="connection-progress tone-active" aria-live="polite">
        <span className="connection-progress-dot"><i /></span>
        <div><strong>浏览器已连接，正在启动评测</strong><p>Agent 即将开始执行用户场景，不需要再次点击。</p></div>
      </div>
    );
  }

  if (status === "ready" || autoConnection?.stage === "connected") {
    return (
      <div className="connection-progress tone-success" aria-live="polite">
        <span className="connection-progress-dot"><Check size={16} weight="bold" /></span>
        <div><strong>目标网页连接成功</strong><p>切换到被测网页可以看到插件控制的标签页；回到这里点击“开始评测”。</p></div>
      </div>
    );
  }

  if (autoConnection?.stage === "failed") {
    return (
      <div className="connection-progress tone-danger" aria-live="polite">
        <span className="connection-progress-dot"><WarningCircle size={17} weight="fill" /></span>
        <div><strong>自动连接没有完成</strong><p>{autoConnection.error || "请确认新版插件已经启用，然后重新连接。"}</p></div>
      </div>
    );
  }

  const copy = {
    opening: ["正在把任务交给插件", "插件将自动打开这次评测的目标网页。"],
    pairing: ["正在验证一次性授权", "不需要手动填写会话 ID 或配对码。"],
    waiting: ["正在等待浏览器连接", "目标网页打开后通常几秒内完成。"],
  }[autoConnection?.stage || "waiting"];

  return (
    <div className="connection-progress tone-active" aria-live="polite">
      <span className="connection-progress-dot"><i /></span>
      <div><strong>{copy[0]}</strong><p>{copy[1]}</p></div>
    </div>
  );
}

function CopyValue({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className={emphasis ? "copy-value emphasis" : "copy-value"}>
      <span>{label}</span><strong>{value}</strong>
      <button onClick={copy} aria-label={`复制${label}`}>{copied ? <Check size={17} /> : <Copy size={17} />}</button>
    </div>
  );
}

function InfoBlock({ icon, label, value, link = false }: { icon: ReactNode; label: string; value: string; link?: boolean }) {
  return (
    <div className="info-block">
      <span className="info-icon">{icon}</span>
      <div><span>{label}</span>{link ? <a href={value} target="_blank" rel="noreferrer">{value}</a> : <strong>{value}</strong>}</div>
    </div>
  );
}

function FailurePanel({ evaluation, onRetry }: { evaluation: Evaluation; onRetry: () => void }) {
  const failure = recordValue(evaluation.downstreamStatus);
  const stage = typeof failure?.stage === "string" ? failure.stage : "unknown";
  const evaluatorOnly = evaluation.error === "evaluator_start_failed" || evaluation.error === "evaluator_runtime_failed";
  return (
    <section className="panel failure-panel" role="alert">
      <div className="failure-icon"><WarningCircle size={22} weight="fill" /></div>
      <div>
        <h2>这次评测没有完成</h2>
        <p>{failureStageLabel(stage)}。{evaluatorOnly ? "用户模拟证据已保留，重试只会重新运行独立评估，不会再操作被测网页。" : "可以保留原配置，重新连接浏览器后再试一次。"}</p>
        <details><summary>查看技术信息</summary><code>{evaluation.error || String(failure?.code || "unknown_error")}</code></details>
      </div>
      <button className="button button-primary" onClick={onRetry}><Play size={17} weight="fill" />{evaluatorOnly ? "重试独立评估" : "原配置重试"}</button>
    </section>
  );
}

function Timeline({ evaluation, events }: { evaluation: Evaluation; events: LocalEvent[] }) {
  const all = events.length ? events : [{
    id: 0,
    type: "evaluation.created",
    payload: {},
    createdAt: evaluation.createdAt,
  }];
  return (
    <div className="timeline">
      {all.map((event, index) => (
        <div className="timeline-row" key={`${event.id}-${event.type}`}>
          <span className={index === all.length - 1 && !terminalStatuses.has(evaluation.status) ? "timeline-node active" : "timeline-node"} />
          <div><strong>{eventLabel(event.type)}</strong><span>{formatTime(event.createdAt)}</span></div>
        </div>
      ))}
      {evaluation.status === "running" && (
        <div className="timeline-row"><span className="timeline-node active" /><div><strong>正在等待并跟进被测 Agent</strong><span>长任务会持续监听，稳定回复后才进入下一轮</span></div></div>
      )}
      {evaluation.status === "evaluating" && (
        <div className="timeline-row"><span className="timeline-node active" /><div><strong>独立评估器正在归纳证据</strong><span>报告生成中</span></div></div>
      )}
    </div>
  );
}

const defaultEvaluationValues = {
  productName: "",
  targetUrl: "",
  taskQuery: "帮我创建一个拳皇类似题材的互动影游",
  experienceBrief: "目标用户通过产品主流程完成任务，获得真实可见、可继续操作的结果。",
  scenarioName: "完成一次从输入创作要求到获得可预览结果的核心流程",
  personaName: "第一次使用产品、目标明确但不了解内部功能的创作者",
  monitoringProfile: "generic" as const,
  responseDeadlineSeconds: 1800,
};

function NewEvaluation({ initialTemplateId, onCreated }: { initialTemplateId: string; onCreated: (created: CreatedEvaluation) => void }) {
  const initialTemplate = evaluationTemplates.find((template) => template.id === initialTemplateId);
  const [templateId, setTemplateId] = useState(initialTemplate?.id ?? "");
  const [values, setValues] = useState(initialTemplate ? {
    productName: initialTemplate.productName,
    targetUrl: initialTemplate.targetUrl,
    taskQuery: initialTemplate.taskQuery,
    experienceBrief: initialTemplate.experienceBrief,
    scenarioName: initialTemplate.scenarioName,
    personaName: initialTemplate.personaName,
    monitoringProfile: initialTemplate.monitoringProfile,
    responseDeadlineSeconds: initialTemplate.responseDeadlineSeconds,
  } : {
    ...defaultEvaluationValues,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extensionInfo, setExtensionInfo] = useState<ExtensionInfo | null>(null);
  const [checkingExtension, setCheckingExtension] = useState(true);

  const refreshExtension = useCallback(async () => {
    setCheckingExtension(true);
    const detected = await detectExtension();
    setExtensionInfo(detected);
    setCheckingExtension(false);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeExtension((info) => {
      setExtensionInfo(info);
      setCheckingExtension(false);
    });
    void refreshExtension();
    return unsubscribe;
  }, [refreshExtension]);

  const extensionReady = Boolean(extensionInfo && versionAtLeast(extensionInfo.version, "0.3.2"));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!extensionReady) {
      setError("请先安装或更新 EchoLab 浏览器插件，然后刷新本页。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await api.createEvaluation(values);
      onCreated(response);
    } catch (caught) {
      setError(readableError(caught));
    } finally {
      setBusy(false);
    }
  };

  const update = (key: "productName" | "targetUrl" | "taskQuery" | "experienceBrief" | "scenarioName" | "personaName", value: string) => setValues((current) => ({ ...current, [key]: value }));
  const applyTemplate = (nextId: string) => {
    setTemplateId(nextId);
    const template = evaluationTemplates.find((item) => item.id === nextId);
    setValues(template ? {
      productName: template.productName,
      targetUrl: template.targetUrl,
      taskQuery: template.taskQuery,
      experienceBrief: template.experienceBrief,
      scenarioName: template.scenarioName,
      personaName: template.personaName,
      monitoringProfile: template.monitoringProfile,
      responseDeadlineSeconds: template.responseDeadlineSeconds,
    } : { ...defaultEvaluationValues });
  };

  return (
    <div className="form-page">
      <div className="page-intro"><h2>创建一次真实用户模拟</h2><p>填写被测网页和创作目标，点击一次即可自动打开网页、连接浏览器并开始评测。</p></div>
      <form className="panel evaluation-form" onSubmit={submit}>
        <div className="form-section">
          <h3>评测模板</h3>
          <label className="field">
            <span>选择配置</span>
            <select value={templateId} onChange={(event) => applyTemplate(event.target.value)}>
              <option value="">通用网页创作流程</option>
              {evaluationTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
            <small>选择模板后会自动填写产品期望体验、用户场景和用户画像，仍可继续修改。</small>
          </label>
        </div>
        <div className="form-section">
          <h3>被测产品</h3>
          <div className="form-grid two-column">
            <label className="field"><span>产品名称</span><input value={values.productName} onChange={(event) => update("productName", event.target.value)} placeholder="例如：Tide Studio" required /></label>
            <label className="field"><span>网页链接</span><input type="url" value={values.targetUrl} onChange={(event) => update("targetUrl", event.target.value)} placeholder="https://example.com/app" required /><small>托管模式只支持公开的 http 或 https 页面</small></label>
          </div>
        </div>
        <div className="form-section">
          <h3>模拟方式</h3>
          <label className="field"><span>产品期望的用户体验</span><textarea value={values.experienceBrief} onChange={(event) => update("experienceBrief", event.target.value)} rows={5} required /><small>先说清目标用户、理想路径和成功终态；Agent 会按这份体验基线模拟，不会随机乱点。</small></label>
          <label className="field"><span>要输入给产品的话</span><textarea value={values.taskQuery} onChange={(event) => update("taskQuery", event.target.value)} rows={3} required /><small>Agent 会把这句话原样输入被测产品，不再混在流程说明里。</small></label>
          <label className="field"><span>用户场景</span><textarea value={values.scenarioName} onChange={(event) => update("scenarioName", event.target.value)} rows={4} required /></label>
          <label className="field"><span>用户画像</span><textarea value={values.personaName} onChange={(event) => update("personaName", event.target.value)} rows={3} required /></label>
        </div>
        <div className="mode-summary">
          <div><ShieldCheck size={22} /><span><strong>官方托管模式</strong><small>使用 EchoLab Agent 配置和你的浏览器标签页</small></span></div>
          <span className="mode-selected"><Check size={15} />已选择</span>
        </div>
        <div className={`extension-readiness ${extensionReady ? "is-ready" : checkingExtension ? "is-checking" : "is-missing"}`} aria-live="polite">
          <span className="extension-readiness-icon">{extensionReady ? <Check size={18} weight="bold" /> : <PuzzlePiece size={18} />}</span>
          <div>
            <strong>{extensionReady ? "浏览器插件已就绪" : checkingExtension ? "正在检测浏览器插件" : "没有检测到新版插件"}</strong>
            <small>{extensionReady
              ? `版本 ${extensionInfo?.version}，创建后将自动打开并连接目标网页。`
              : checkingExtension
                ? "通常只需要一秒。"
                : "请安装 0.3.2 或更高版本，安装完成后刷新这个页面。"}</small>
          </div>
          {!extensionReady && !checkingExtension && (
            <div className="extension-readiness-actions">
              <a className="button button-secondary" href="/extension" target="_blank" rel="noreferrer">下载新版插件</a>
              <button className="text-action" type="button" onClick={() => window.location.reload()}>刷新检测</button>
            </div>
          )}
        </div>
        {error && <p className="inline-error"><WarningCircle size={17} />{error}</p>}
        <div className="form-actions"><button className="button button-primary button-large" disabled={busy || !extensionReady}>{busy ? "正在创建" : extensionReady ? "创建并开始评测" : "请先安装新版插件"}<ArrowRight size={18} /></button></div>
      </form>
    </div>
  );
}

function Configurations({ onUseTemplate }: { onUseTemplate: (templateId: string) => void }) {
  return (
    <div className="config-page">
      <div className="page-intro"><h2>当前评测配置</h2><p>托管模式固定使用已发布的模拟器、独立评估器和三项评测 Skills。</p></div>
      <section className="panel config-hero">
        <div><span className="config-type">官方配置</span><h3>网页创作流程评测</h3><p>适合有输入、Agent 回复、页面状态变化和最终产物预览的网页产品。</p></div>
        <StatusBadge status="ready" customLabel="已发布" />
      </section>
      <section className="config-grid">
        <div className="config-block"><span>用户模拟器</span><strong>EchoLab 用户模拟器 v0.1</strong><p>像真实用户一样执行创作流程并记录证据。</p></div>
        <div className="config-block"><span>独立评估器</span><strong>EchoLab 独立评估器 v0.1</strong><p>只读取证据，不参与页面操作和结果修饰。</p></div>
        <div className="config-block wide"><span>评测 Skills</span><div className="skill-list"><code>run-user-scenario</code><code>evaluate-product-response</code><code>summarize-evaluation-run</code></div></div>
      </section>
      <div className="template-section-heading"><h3>产品模板</h3><p>选择后直接带入新建评测，模板内容仍可在提交前调整。</p></div>
      <section className="panel template-list">
        {evaluationTemplates.map((template) => (
          <article className="template-row" key={template.id}>
            <div className="template-main">
              <span className="config-type">测试环境</span>
              <strong>{template.name}</strong>
              <a href={template.targetUrl} target="_blank" rel="noreferrer">{template.targetUrl}</a>
              <p><strong>期望体验：</strong>{template.experienceBrief}</p>
              <p>{template.scenarioName}</p>
              <p><strong>监听方式：</strong>{template.monitoringProfile === "nexplay" ? "Nexplay 长任务事件监听" : "通用页面监听"} · 单轮最长等待 {Math.round(template.responseDeadlineSeconds / 60)} 分钟</p>
            </div>
            <button className="button button-secondary" onClick={() => onUseTemplate(template.id)}>使用模板<ArrowRight size={17} /></button>
          </article>
        ))}
      </section>
    </div>
  );
}

function EmptyRuns({ onNew }: { onNew: () => void }) {
  return (
    <section className="empty-state">
      <div className="empty-icon"><Flask size={34} /></div>
      <h2>还没有评测任务</h2>
      <p>创建一次评测，连接浏览器插件，然后让 Agent 按真实用户流程操作产品。</p>
      <button className="button button-primary button-large" onClick={onNew}><Plus size={19} />新建评测</button>
    </section>
  );
}

function RunsSkeleton() {
  return <div className="runs-layout" aria-label="正在加载评测任务"><div className="panel skeleton-panel" /><div className="skeleton-stack"><div /><div /><div /></div></div>;
}

function StatusBadge({ status, customLabel }: { status: EvaluationStatus; customLabel?: string }) {
  const meta = statusMeta[status];
  return <span className={`status-badge tone-${meta.tone}`}>{customLabel || meta.label}</span>;
}

function replaceEvaluation(items: Evaluation[], next: Evaluation): Evaluation[] {
  return items.some((item) => item.id === next.id)
    ? items.map((item) => item.id === next.id ? next : item)
    : [next, ...items];
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(value));
}

function eventLabel(type: string): string {
  return {
    "evaluation.created": "评测任务已创建",
    "evaluation.started": "Maxwell Agent 已启动",
    "simulation.completed": "用户模拟已完成，浏览器控制已释放",
    "simulation.response_checkpoint": "已保存一条产品回复检查点",
    "simulation.partial": "用户模拟中断，已转入部分报告",
    "evaluator.started": "独立评估器已启动",
    "evaluation.completed": "评测报告已生成",
    "evaluation.report_reparsed": "评测报告已自动修复",
    "evaluation.failed": "评测运行失败",
    "evaluation.retry_created": "已用原配置重新创建运行会话",
    "evaluator.retry_started": "已保留用户模拟证据，正在重试独立评估",
    "evaluator.start_deferred": "评估服务暂时繁忙，已自动延迟重试",
    "evaluation.stopped": "评测已人工停止",
    "evaluation.partial_report_generated": "已生成中断前的部分报告",
  }[type] || type;
}

function failureStageLabel(stage: string): string {
  return {
    maxwell_thread_create: "创建 Maxwell 任务时失败",
    maxwell_simulator_start: "启动用户模拟器时失败",
    startup_timeout: "评测启动超时",
    simulator: "用户模拟执行过程中失败",
    evaluator_start: "启动独立评估器时失败",
    evaluator: "独立评估与报告生成过程中失败",
  }[stage] || "评测服务返回了异常";
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function recordList(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.map(recordValue).filter((item): item is Record<string, unknown> => Boolean(item)) : [];
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && Boolean(item.trim())) : [];
}

function textValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function numberText(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "-";
}

function percentValue(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value) ? `${Math.round(value * 100)}%` : "-";
}

function compactValue(value: unknown): string {
  if (Array.isArray(value)) return value.map((item) => String(item)).join("、");
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function severityLabel(value?: string): string {
  return { critical: "严重", high: "高", medium: "中", low: "低", info: "提示" }[value || "info"] || value || "提示";
}

function verdictLabel(value: string | null): string {
  return { pass: "通过", partial: "部分通过", fail: "未通过", not_evaluable: "不可评估" }[value || ""] || value || "未评价";
}

function confidenceLabel(value: string): string {
  return { high: "高", medium: "中", low: "低" }[value] || value;
}

function versionAtLeast(current: string, required: string): boolean {
  const currentParts = current.split(".").map((part) => Number(part) || 0);
  const requiredParts = required.split(".").map((part) => Number(part) || 0);
  for (let index = 0; index < Math.max(currentParts.length, requiredParts.length); index += 1) {
    const difference = (currentParts[index] || 0) - (requiredParts[index] || 0);
    if (difference !== 0) return difference > 0;
  }
  return true;
}

function readableExtensionError(error: unknown): string {
  if (error instanceof ApiError) return readableError(error);
  const message = error instanceof Error ? error.message : "extension_connection_failed";
  if (message === "extension_not_detected") return "没有检测到新版浏览器插件，请确认插件已启用并刷新 EchoLab。";
  if (message === "extension_connection_timeout") return "浏览器连接超时，请确认目标网页能够正常打开后重试。";
  if (message.includes("只允许 EchoLab 官方页面")) return "插件拒绝了非官方页面的连接请求。";
  return message && !message.startsWith("extension_") ? message : "浏览器插件没有完成连接，请重试。";
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function loadSessionWithRetry() {
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const session = await api.session();
      if (session.authenticated || attempt === 2) return session;
    } catch (error) {
      lastError = error;
      if (error instanceof ApiError && error.status === 401) throw error;
      if (attempt === 2) throw error;
    }
    await delay(250 * (attempt + 1));
  }
  throw lastError ?? new Error("session_check_failed");
}
